import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fleet, type CommandBatch, type CommandLogRow } from "@/api/client";
import { PageHead } from "@/components/PageHead";
import { DataTable, type DataCol } from "@/components/DataTable";
import { CAP, useSession } from "@/auth/session";
import { POLL, ago } from "@/lib/format";
import { BulkPanel } from "@/modules/fleet/BulkPanel";
import { ACT_LABEL, BULK_COMMANDS, COMMANDS, LEVEL_LABEL, STATE_MEANS,
         StateChip, reasonText } from "@/modules/fleet/catalogue";

/** Fleet · Commands — act on MANY assets, and watch the runs land.
 *
 *  THE SPLIT WITH ASSET CONTROL IS BY JOB, not by convenience. This page is
 *  for "stop that depot" and "what did that run actually do". Asset Control is
 *  for one asset: its state, its holds, its history. The two have genuinely
 *  different shapes — a fleet view needs batch rollups and a scannable log, a
 *  single-asset view needs a state banner that cannot lie — and forcing them
 *  into one page is what made the earlier draft unreadable.
 *
 *  BOTH PAGES RENDER THEIR CONTROLS FROM THE CATALOGUE, so a new command is
 *  one entry there: it appears here only if its scope says it can run in bulk,
 *  and on Asset Control either way.
 *
 *  POLL CADENCE: the batch table is operational state (POLL.STATE); the log is
 *  a list that a human reads (POLL.LIST). Budgeting them differently is the UI
 *  performance law, not a preference — two 2 s polls on one page is how the
 *  console earned that law.
 */
export function FleetCommandsPage() {
  const { can } = useSession();
  const mayCommand = can(CAP.IMMOBILIZE);
  const [cmdKey, setCmdKey] = useState(BULK_COMMANDS[0]?.key ?? "immobilize");
  const [activeOnly, setActiveOnly] = useState(false);

  const spec = BULK_COMMANDS.find((c) => c.key === cmdKey) ?? BULK_COMMANDS[0];

  const batches = useQuery({
    queryKey: ["fleet", "batches"],
    queryFn: () => fleet.batches(),
    refetchInterval: POLL.STATE,
  });
  const log = useQuery({
    queryKey: ["fleet", "log", activeOnly],
    queryFn: () => fleet.commands({ active: activeOnly }),
    refetchInterval: POLL.LIST,
  });

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Fleet · Commands"
        title="Run a command across the fleet"
        desc={<>Pick a command, choose who it applies to, review what will
          actually be sent, then run it. Nothing is in force until the device
          says <b>applied</b> — a command the device is holding is still a
          moving vehicle.</>}
        right={<Link className="btn ghost" to="/fleet/control">Asset Control</Link>}
      />

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start",
                    flexWrap: "wrap" }}>
        {/* THE CATALOGUE, rendered. Not hand-built buttons: a command appears
            here because its entry says `scope: "both"`. */}
        <div style={{ flex: "1 1 260px", maxWidth: 320, minWidth: 250 }}>
          <div className="section-label">Command</div>
          <div className="panel" style={{ overflow: "hidden" }}>
            {COMMANDS.map((c) => {
              const bulkable = c.scope === "both";
              const chosen = bulkable && c.key === cmdKey;
              return (
                <button key={c.key} type="button"
                  disabled={!bulkable || !c.built}
                  onClick={() => setCmdKey(c.key)}
                  title={!bulkable
                    ? `${c.label} runs one asset at a time — use Asset Control`
                    : !c.built ? `${c.label} is not built yet` : undefined}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "12px 14px", border: 0,
                    borderTop: "1px solid var(--line)",
                    borderLeft: chosen ? "3px solid var(--bad)" : "3px solid transparent",
                    background: chosen ? "color-mix(in srgb, var(--bad) 8%, transparent)"
                                      : "transparent",
                    cursor: bulkable && c.built ? "pointer" : "not-allowed",
                    opacity: bulkable && c.built ? 1 : 0.55, font: "inherit",
                  }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 700 }}>
                    {c.label}
                  </span>
                  <span style={{ display: "block", fontSize: 11.5,
                                 color: "var(--t-mute)", marginTop: 2 }}>
                    {c.hint}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--t-mute)", margin: "8px 2px 0" }}>
            One-shot commands are single-only on purpose: firing one at two
            hundred assets gives a result nobody can act on.
          </div>
        </div>

        <div style={{ flex: "999 1 560px", minWidth: 0 }}>
          <div className="section-label">Who it applies to</div>
          {spec && <BulkPanel spec={spec} mayCommand={mayCommand} />}
        </div>
      </div>

      <div className="section-label">Batches</div>
      <BatchTable rows={batches.data?.batches ?? []} loading={batches.isLoading} />

      <div className="section-label">
        All commands — newest first
        <label style={{ marginLeft: 12, fontSize: 11.5, fontWeight: 400,
                        textTransform: "none", letterSpacing: 0 }}>
          <input type="checkbox" checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)} />
          {" "}still able to change only
        </label>
      </div>
      <LogTable rows={log.data?.commands ?? []} loading={log.isLoading}
        hasMore={log.data?.has_more ?? false} />
    </div>
  );
}

function BatchTable({ rows, loading }: { rows: CommandBatch[]; loading: boolean }) {
  // module-stable identity is the DataTable contract (memoized rows); these
  // close over nothing, so useMemo with an empty dep list is honest here
  const cols = useMemo<DataCol<CommandBatch>[]>(() => [
    { key: "when", label: "Started", width: 90,
      render: (b) => ago(b.created_at) },
    { key: "cmd", label: "Command", width: 180,
      render: (b) => `${b.type === "immobilize" ? "Immobilise" : b.type} · ${
        ACT_LABEL[b.action] ?? b.action}` },
    { key: "by", label: "By", width: 150, mono: true, render: (b) => b.created_by },
    { key: "level", label: "Level", width: 110,
      render: (b) => b.level != null ? (LEVEL_LABEL[b.level] ?? `level ${b.level}`) : "—" },
    // REQUESTED AND COMMANDED ARE SHOWN APART, frozen as of the run. A single
    // merged number would hide that assets were skipped.
    { key: "size", label: "Commanded", width: 120,
      title: (b) => `${b.requested} requested, ${b.skipped} skipped`,
      render: (b) => <>{b.commanded}<span className="muted">
        {" "}of {b.requested}</span></> },
    { key: "applied", label: "Applied", width: 95,
      render: (b) => b.applied > 0
        ? <StateChip state="applied" />
        : <span className="muted">—</span> },
    { key: "armed", label: "Waiting for halt", width: 130,
      title: () => STATE_MEANS.received,
      render: (b) => b.armed > 0
        ? <span className="chip" style={{ color: "var(--warn)",
            background: "color-mix(in srgb, var(--warn) 13%, transparent)" }}>
            {b.armed} armed</span>
        : <span className="muted">—</span> },
    { key: "refused", label: "Refused", width: 95,
      render: (b) => b.refused > 0
        ? <span className="chip" style={{ color: "var(--bad)",
            background: "color-mix(in srgb, var(--bad) 13%, transparent)" }}>
            {b.refused}</span>
        : <span className="muted">—</span> },
    { key: "done", label: "Run", width: 95,
      render: (b) => b.finished
        ? <span className="muted">finished</span>
        : <b style={{ color: "var(--warn)" }}>open</b> },
    { key: "batch", label: "Batch", width: 90, mono: true,
      title: (b) => b.batch_id,
      render: (b) => b.batch_id.slice(0, 8) },
  ], []);

  if (!loading && rows.length === 0) {
    return (
      <div className="panel panel-pad cap">
        No bulk runs yet. A run appears here the moment it starts, and stays
        <b> open</b> until no command under it can change.
      </div>
    );
  }
  return <DataTable rows={rows} cols={cols} rowKey={(b) => b.batch_id} />;
}

function LogTable({ rows, loading, hasMore }: {
  rows: CommandLogRow[]; loading: boolean; hasMore: boolean;
}) {
  const cols = useMemo<DataCol<CommandLogRow>[]>(() => [
    { key: "when", label: "When", width: 85, render: (c) => ago(c.created_at) },
    { key: "asset", label: "Asset", width: 180, mono: true,
      title: (c) => c.asset_id ?? c.connection_id,
      render: (c) => c.asset_id
        ? <Link to={`/fleet/control?asset=${encodeURIComponent(c.asset_id)}`}>
            {c.asset_id}</Link>
        // NO ASSET MEANS THE BOX WAS COMMANDED BEFORE ITS ASSET WAS KNOWN.
        // Showing the IMEI rather than a blank keeps the row useful instead
        // of looking like missing data.
        : <span title="commanded by connection — no asset recorded">
            {c.connection_id}</span> },
    { key: "cmd", label: "Command", width: 185,
      render: (c) => `${c.type === "immobilize" ? "Immobilise" : c.type} · ${
        ACT_LABEL[c.action] ?? c.action}` },
    { key: "level", label: "Level", width: 110,
      render: (c) => c.level != null ? (LEVEL_LABEL[c.level] ?? `level ${c.level}`) : "—" },
    { key: "state", label: "State", width: 105, render: (c) => <StateChip state={c.state} /> },
    // THE "MEANS" COLUMN IS THE POINT. "expired_unapplied" tells an operator
    // nothing; "device had it and gave up" tells them what to do next.
    { key: "means", label: "Means", width: 240,
      title: (c) => STATE_MEANS[c.state] ?? c.state,
      render: (c) => <span style={{ fontSize: 12 }}>{STATE_MEANS[c.state] ?? "—"}</span> },
    { key: "reason", label: "Reason", width: 140,
      render: (c) => reasonText(c.reason_code) },
    { key: "batch", label: "Batch", width: 85, mono: true,
      title: (c) => c.batch_id ?? "single command",
      render: (c) => c.batch_id
        ? c.batch_id.slice(0, 8)
        : <span className="muted">—</span> },
  ], []);

  if (!loading && rows.length === 0) {
    return <div className="panel panel-pad cap">No commands recorded yet.</div>;
  }
  return (
    <>
      <DataTable rows={rows} cols={cols} rowKey={(c) => c.cmd_id} pageSize={25} />
      {hasMore && (
        <div style={{ fontSize: 11.5, color: "var(--t-mute)", margin: "8px 2px 0" }}>
          Showing the most recent {rows.length}. Older commands are on the
          asset's own page — this log is a live view, not an archive.
        </div>
      )}
    </>
  );
}
